import React from "react";
import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import { InView } from "react-intersection-observer";
// import { useQuery } from "@apollo/client";
import { useLocation } from "react-router-dom";
// import { ContentQuery } from "arena/queries";
import ShortyPlayer from "./ShortyPlayer";
import { CloseRouteDialogButton } from "arena/components/RouteDialog";
import contentUrl from "core/utils/contentUrl";
import { useDesktopHeaderHeight } from "arena/components/AppShell/AppModeContext";

export default function ShortyVirtualItem({
  content,
  productId,
  collectionId,
  index = 0,
  isActive = false,
  shouldPreload = false,
  onActivate,
  onNavigate,
  totalItems,
  mainVideoRef = null,
  isMainVideoMounted = false,
  onActiveVideoReady = null, // Callback для передачи данных активного видео
}) {
  const theme = useTheme();
  const isMobileView = useMediaQuery(theme.breakpoints.down("md"));
  const headerHeight = useDesktopHeaderHeight();
  const location = useLocation();

  // Функции навигации
  const handlePrevious = () => {
    if (index > 0 && onNavigate) {
      onNavigate(index - 1);
    }
  };

  const handleNext = () => {
    if (index < totalItems - 1 && onNavigate) {
      onNavigate(index + 1);
    }
  };

  const canGoPrevious = index > 0;
  const canGoNext = index < totalItems - 1;

  const handleInViewChange = (inView) => {
    // Активируем когда элемент входит в зону видимости
    if (inView && !isActive && content && onActivate) {
      onActivate(); // Говорим родителю что этот элемент должен стать активным

      // Обновляем URL когда элемент становится активным
      const expectedUrl = contentUrl(content.siteUrl, {
        productId,
        collectionId,
      });

      // Проверяем нужно ли обновить URL
      const currentPath = location.pathname + location.search;
      if (currentPath !== expectedUrl) {
        // Используем history API напрямую чтобы избежать ререндера
        window.history.replaceState({}, "", expectedUrl);
      }
    }
  };

  return (
    <InView threshold={0.85} onChange={handleInViewChange} triggerOnce={false}>
      <Box
        data-video-container // Добавляем атрибут для позиционирования главного video
        sx={{
          width: "100%",
          height: isMobileView
            ? "100dvh" // Dynamic viewport height для мобильных
            : `calc(100vh - ${headerHeight}px)`, // На десктопе обычный vh минус header
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          scrollSnapAlign: "start",
          scrollSnapStop: "always",
          flexShrink: 0, // Предотвращаем сжатие элемента
          position: "relative",
        }}
      >
        <Box
          sx={{
            position: "relative",
            height: "100%",
            maxWidth: "100%",
            // На десктопе удерживаем вертикальное видео в соотношении 9:16 от высоты
            ...(isMobileView ? { width: "100%" } : { aspectRatio: "9 / 16" }),
          }}
        >
          <ShortyPlayer
            content={content}
            active={isActive}
            preload={shouldPreload && !isActive} // Предзагрузка для неактивного элемента
            mainVideoRef={isActive ? mainVideoRef : null}
            isMainVideoMounted={isActive ? isMainVideoMounted : false}
            onActiveVideoReady={isActive ? onActiveVideoReady : null} // Только для активного элемента
            closeRouteDialogButton={
              isActive ? <CloseRouteDialogButton /> : null
            }
            onPrevious={isActive ? handlePrevious : null}
            onNext={isActive ? handleNext : null}
            canGoPrevious={isActive ? canGoPrevious : false}
            canGoNext={isActive ? canGoNext : false}
          />
        </Box>
      </Box>
    </InView>
  );
}

ShortyVirtualItem.propTypes = {
  content: PropTypes.shape({
    id: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    siteUrl: PropTypes.string.isRequired,
    verticalThumbnails: PropTypes.array,
    thumbnails: PropTypes.array,
  }),
  productId: PropTypes.string,
  collectionId: PropTypes.string,
  index: PropTypes.number,
  isActive: PropTypes.bool,
  shouldPreload: PropTypes.bool,
  onActivate: PropTypes.func,
  onNavigate: PropTypes.func,
  totalItems: PropTypes.number,
  mainVideoRef: PropTypes.object,
  isMainVideoMounted: PropTypes.bool,
  onActiveVideoReady: PropTypes.func,
};
